import { Text, Button, Modal, Stack, Title, Group, Divider, Card, SimpleGrid } from '@mantine/core';
import {
  IconUserPentagon,
  IconUsers,
  IconCurrencyDollar,
  IconBed,
  IconSparkles,
  IconClockPin,
  IconAperture,
  IconHelp,
} from '@tabler/icons-react';
import { downloadPdfFromElement } from '@lib/download-pdf';
import { TripProfile } from '@generated/graphql';
import { ItineraryItem } from '@src/types/itinerary';
import { format } from 'date-fns';
import ItineraryTable from './itinerary-table';
import classes from './print-trip-modal.module.css';

interface TripDetailCardProps {
  icon: React.ReactNode;
  title: string;
  text: string | number;
}
function TripDetailCard({ icon, title, text }: TripDetailCardProps) {
  return (
    <Card shadow="sm" padding="lg" radius="md" withBorder>
      <Group className={classes.card}>
        {icon}
        <Text>{title}</Text>
      </Group>
      <Text ml={36}>{text}</Text>
    </Card>
  );
}

type Props = {
  opened: boolean;
  onClose: () => void;
  tripProfile: TripProfile | null;
  itinerary?: ItineraryItem[] | null;
  pdfFilename?: string;
};

function ItineraryPreviewModal({
  opened,
  onClose,
  tripProfile,
  itinerary = undefined,
  pdfFilename = 'trip-details',
}: Props) {
  if (!tripProfile) {
    return null;
  }
  const handleDownload = () => {
    console.log('Downloading PDF...');
    downloadPdfFromElement('printable-itinerary', `${pdfFilename}.pdf`).catch((error) => {
      console.error('Error downloading PDF:', error);
    });
  };

  const handlePrint = () => {
    window.print();
  };

  return (
    <Modal opened={opened} onClose={onClose} size="97%" centered title="Trip Itinerary Preview">
      <Stack gap="md">
        <div id="printable-itinerary" className="printable-content">
          <Title order={1}>{tripProfile.title}</Title>
          <Title order={2}>
            {tripProfile.destination}
            {tripProfile.country ? `, ${tripProfile.country}` : ''}
          </Title>
          <Divider my="lg" />
          <Group justify="space-between" mb={20}>
            <Text>
              {tripProfile.startDate ? format(new Date(tripProfile.startDate), 'MMMM d, yyyy') : 'N/A'} to{' '}
              {tripProfile.endDate ? format(new Date(tripProfile.endDate), 'MMMM d, yyyy') : 'N/A'}
            </Text>
            <Text>TRIP STATUS: {tripProfile.status}</Text>
          </Group>
          <Divider my="lg" />
          <Title order={4}>Client Intake Details</Title>
          <SimpleGrid cols={4} mb={20}>
            <TripDetailCard
              icon={<IconUserPentagon size={18} />}
              title="Group Type"
              text={tripProfile.travelPartyType ?? 'N/A'}
            />
            <TripDetailCard
              icon={<IconUsers size={18} />}
              title="Number of Travelers"
              text={tripProfile.numberTravelers ?? 'N/A'}
            />
            <TripDetailCard
              icon={<IconCurrencyDollar size={18} />}
              title="Budget"
              text={`$${new Intl.NumberFormat().format(tripProfile.budgetLower ?? 0)} to $${new Intl.NumberFormat().format(tripProfile.budgetUpper ?? 0)}`}
            />
            <TripDetailCard
              icon={<IconBed size={18} />}
              title="Accommodations"
              text={tripProfile.accommodationType ?? 'N/A'}
            />
            <TripDetailCard
              icon={<IconSparkles size={18} />}
              title="Occasion"
              text={tripProfile.occasionType ?? 'N/A'}
            />
            <TripDetailCard
              icon={<IconClockPin size={18} />}
              title="Schedule Density"
              text={tripProfile.scheduleDensity ?? 'N/A'}
            />
            <TripDetailCard
              icon={<IconAperture size={18} />}
              title="Cultural Level"
              text={tripProfile.culturalComfortLevel ?? 'N/A'}
            />
            <TripDetailCard
              icon={<IconHelp size={18} />}
              title="Support Level"
              text={tripProfile.supportLevel ?? 'N/A'}
            />
          </SimpleGrid>

          {itinerary && (
            <>
              <Title order={3}>Trip Itinerary</Title>
              <ItineraryTable itinerary={itinerary} />
            </>
          )}
        </div>

        <Group>
          <Button onClick={handlePrint} variant="outline">
            Print
          </Button>
          <Button onClick={handleDownload}>Download PDF</Button>
          <Button onClick={onClose}>Close</Button>
        </Group>
      </Stack>
    </Modal>
  );
}

export default ItineraryPreviewModal;
